// tslint:disable:typedef
// tslint:disable:arrow-parens
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AuthGuardService } from '@core/authentication/auth-guard.service';
import { LayoutComponent } from '@core/layout/layout.component';
import { ReportRouter } from '@core/routers/report-router.enum';
import { TermsAndPolicyGuardService } from '@features/login/shared/terms-and-policy-guard.service';
import { PageForbiddenComponent } from '@features/page-forbidden/page-forbidden.component';
import { PageInternalServerErrorComponent } from '@features/page-internal-server-error/page-internal-server-error.component';
import { PageNotFoundComponent } from '@features/page-not-found/page-not-found.component';
import { PageUnauthorizedComponent } from '@features/page-unauthorized/page-unauthorized.component';
import { ReportsStandaloneModule } from '@features/report-new/report-new-standalone.module';
import { AllowOnlyPartnerListGuardService } from '@features/report-new/shared/allow-only-partner-list-guard.service';
import { ReportCheckIEBrowserGuardService } from '@features/report-new/shared/report-check-ie-browser-guard.service';
import { SettingsSaveTonerBehaviorEnabledGuardService } from '@features/settings/save-toner/shared/settings-save-toner-behavior-enabled-guard.service';
import { SettingsSaveTonerGuardService } from '@features/settings/save-toner/shared/settings-save-toner-guard.service';
import { SettingsSaveTonerLicensingEnabledGuardService } from '@features/settings/save-toner/shared/settings-save-toner-licencing-guard.service';
import { LayoutDefaultGuardService } from '@shared/guards/layout-default-guard.service';
import { NotAllowAdministratorsGuardService } from '@shared/guards/not-allow-administrators-guard.service';
import { NotAllowDisabledManagementPoliciesQuotasGuardService } from '@shared/guards/not-allow-disabled-management-policies-quotas-guard.service';
import { NotAllowLicenseDisabledPoliciesGuardService } from '@shared/guards/not-allow-license-disabled-policies-guard.service';
import { NotAllowManagerWhenAccountIsPrinterTypeGuardService } from '@shared/guards/not-allow-manager-when-account-is-printer-type.service';
import { NotAllowMultiEnterprisesAccessGuardService } from '@shared/guards/not-allow-multi-enterprises-access-guard.service';
import { NotAllowPartnerGuardService } from '@shared/guards/not-allow-partner-guard.service';
import { NotAllowPartnerRestrictGuardService } from '@shared/guards/not-allow-partner-restrict-guard.service';
import { NotAllowUserGuardService } from '@shared/guards/not-allow-user-guard.service';
import { ScanFlowDisabledGuardService } from '@shared/guards/scan-flow-disabled-guard.service';
import { ScanFlowDisabledOrPermissionGuardService } from '@shared/guards/scan-flow-disabled-or-permission-guard.service';
import { SettingsDefaultGuardService } from '@shared/guards/settings-default-guard.service';
import { PageReloadComponent } from '@shared/page-reload/page-reload.component';

const appRoutes: Routes = [
    {
        path: 'login',
        loadChildren: () => import('./features/login/login.module').then(x => x.LoginModule),
    },
    {
        path: 'first-access',
        loadChildren: () => import('./features/first-access/first-access.module').then(x => x.FirstAccessModule),
    },
    {
        path: 'login-mps',
        loadChildren: () => import('./features/login-mps/login-mps.module').then(x => x.LoginMpsModule),
    },
    {
        path: 'reload',
        component: PageReloadComponent,
    },
    {
        path: 'download-export-report',
        loadChildren: () => import('./features/download-export-report/download-export-report.module').then(x => x.DownloadExportReportModule),
    },
    {
        path: 'standalone', // Rota para a nova aba, é preciso ser igual ao path passado no ReportGlobalConfig
        loadChildren: () => import('./features/report-new/report-new-standalone.module').then(x => ReportsStandaloneModule),
        canActivate: [ReportCheckIEBrowserGuardService, AllowOnlyPartnerListGuardService],
    },
    {
        path: '',
        component: LayoutComponent, // LayoutComponent contém a lógica de redirecionamento de rota padrão.
        canActivate: [AuthGuardService, TermsAndPolicyGuardService],
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./features/dashboard/dashboard.module').then(x => x.DashboardModule),
                canLoad: [AuthGuardService, LayoutDefaultGuardService],
                canActivate: [LayoutDefaultGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360DashboardHome',
                    },
                },
            },
            {
                path: 'users',
                loadChildren: () => import('./features/user/user.module').then(x => x.UserModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                    NotAllowDisabledManagementPoliciesQuotasGuardService,
                    NotAllowManagerWhenAccountIsPrinterTypeGuardService,
                ],
                data: {
                    allowManagers: true,
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Users',
                    },
                },
            },
            {
                path: 'user-groups',
                loadChildren: () => import('./features/user-group/user-group.module').then(x => x.UserGroupModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                ],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360UserGroups',
                    },
                },
            },
            {
                path: 'printers',
                loadChildren: () => import('./features/printer/printer.module').then(x => x.PrinterModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                    NotAllowDisabledManagementPoliciesQuotasGuardService,
                ],
                data: {
                    allowManagers: true,
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Printers',
                    },
                },
            },
            {
                path: 'printer-groups',
                loadChildren: () => import('./features/printer-group/printer-group.module').then(x => x.PrinterGroupModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                ],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360PrinterGroup',
                    },
                },
            },
            {
                path: 'machines',
                loadChildren: () => import('./features/machine/machine.module').then(x => x.MachineModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                ],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Machines',
                    },
                },
            },
            {
                path: 'domains',
                loadChildren: () => import('./features/domain/domain.module').then(x => x.DomainModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                ],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Domains',
                    },
                },
            },
            {
                path: 'accounts',
                loadChildren: () => import('./features/account/account.module').then(x => x.AccountModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                    NotAllowDisabledManagementPoliciesQuotasGuardService,
                ],
                data: {
                    allowManagers: true,
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Accounts',
                    },
                },
            },
            {
                path: 'policies',
                loadChildren: () => import('./features/policy/policy.module').then(x => x.PolicyModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowDisabledManagementPoliciesQuotasGuardService,
                    NotAllowLicenseDisabledPoliciesGuardService,
                ],
                data: {
                    dontValidateQuotas: true,
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Policies',
                    },
                    allowManagers: true,
                },
            },
            {
                path: 'settings',
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Settings',
                    },
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'general',
                        pathMatch: 'full',
                    },
                    {
                        path: 'users',
                        loadChildren: () => import('./features/settings/users/settings-users.module').then(x => x.SettingsUsersModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Users',
                            },
                        },
                    },
                    {
                        path: 'my-settings',
                        loadChildren: () =>
                            import('./features/settings/user-configs/settings-user-configs.module').then(x => x.SettingsUserConfigsModule),
                        canLoad: [AuthGuardService],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360MySettings',
                            },
                        },
                    },
                    {
                        path: 'secure-release',
                        loadChildren: () =>
                            import('./features/settings/secure-release/settings-secure-release.module').then(x => x.SettingsSecureReleaseModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360SecureRelease',
                            },
                        },
                    },
                    {
                        path: 'quotas',
                        loadChildren: () => import('./features/settings/quotas/settings-quotas.module').then(x => x.SettingsQuotasModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Quotas',
                            },
                        },
                    },
                    {
                        path: 'printers',
                        loadChildren: () => import('./features/settings/printers/settings-printers.module').then(x => x.SettingsPrintersModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Printers',
                            },
                        },
                    },
                    {
                        path: 'policies',
                        loadChildren: () => import('./features/settings/policies/settings-policies.module').then(x => x.SettingsPoliciesModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                            NotAllowLicenseDisabledPoliciesGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Policies',
                            },
                        },
                    },
                    {
                        path: 'jobs-assignment',
                        loadChildren: () =>
                            import('./features/settings/jobs-assignment/settings-jobs-assignment.module').then(x => x.SettingsJobsAssignmentModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360JobsAssignment',
                            },
                        },
                    },
                    {
                        path: 'general',
                        loadChildren: () => import('./features/settings/general/settings-general.module').then(x => x.SettingsGeneralModule),
                        canLoad: [AuthGuardService, SettingsDefaultGuardService],
                        canActivate: [SettingsDefaultGuardService],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360GeneralP',
                            },
                        },
                    },
                    {
                        path: 'audit',
                        loadChildren: () => import('./features/settings/audit/settings-audit.module').then(x => x.SettingsAuditModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Audit',
                            },
                        },
                    },
                    {
                        path: 'accounting',
                        loadChildren: () => import('./features/settings/accounting/settings-accounting.module').then(x => x.SettingsAccountingModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Accounting',
                            },
                        },
                    },
                    {
                        path: 'digital-shift',
                        loadChildren: () =>
                            import('./features/settings/scan-flow/settings-scan-flow.module').then(x => x.SettingsScanFlowModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                            ScanFlowDisabledOrPermissionGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360ScanFlows',
                            },
                        },
                    },
                    {
                        path: 'eco-toner',
                        loadChildren: () =>
                            import('./features/settings/save-toner/settings-save-toner.module').then(x => x.SettingsSaveTonerModule),
                        canLoad: [AuthGuardService, SettingsSaveTonerGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowAdministratorsGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360SaveToner',
                            },
                        },
                    },
                    {
                        path: 'integrations',
                        // tslint:disable-next-line:max-line-length
                        loadChildren: () => import('./features/settings/integrations/settings-integrations.module').then(x => x.SettingsIntegrationsModule),
                        canLoad: [AuthGuardService],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Integrations',
                            },
                        },
                    },
                    {
                        path: 'domains',
                        loadChildren: () =>
                            import('./features/settings/domains/settings-domains.module').then(x => x.SettingsDomainsModule),
                        canLoad: [AuthGuardService],
                        canActivate: [
                            NotAllowUserGuardService,
                            NotAllowPartnerRestrictGuardService,
                        ],
                        data: {
                            breadcrumbOptions: {
                                breadcrumbLabel: '360Domains',
                            },
                        },
                    },
                ],
            },
            {
                path: ReportRouter.StandardReports,
                loadChildren: () => import('./features/report/report.module').then(x => x.ReportModule),
                canLoad: [AuthGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Reports',
                    },
                },
            },
            {
                path: ReportRouter.NewReports,
                loadChildren: () => import('./features/report-new/report-new.module').then(x => x.ReportNewModule),
                canLoad: [AuthGuardService],
                canActivate: [ReportCheckIEBrowserGuardService, AllowOnlyPartnerListGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Reports',
                    },
                },
            },
            {
                path: 'sites',
                loadChildren: () => import('./features/site/site.module').then(x => x.SiteModule),
                canLoad: [AuthGuardService, NotAllowMultiEnterprisesAccessGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360Sites',
                    },
                },
            },
            {
                path: 'digital-shift',
                loadChildren: () => import('./features/scan-flow/scan-flow.module').then(x => x.ScanFlowModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                    ScanFlowDisabledGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360ScanFlows',
                    },
                },
            },
            {
                path: 'digital-shift-groups',
                loadChildren: () => import('./features/scan-flow-group/scan-flow-group.module').then(x => x.ScanFlowGroupModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowPartnerRestrictGuardService,
                    ScanFlowDisabledGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360ScanFlowGroups',
                    },
                },
            },
            {
                path: 'eco-toner-rules',
                loadChildren: () => import('./features/save-toner-rules/save-toner-rules.module').then(x => x.SaveTonerRulesModule),
                canLoad: [AuthGuardService],
                canActivate: [
                    NotAllowUserGuardService,
                    NotAllowAdministratorsGuardService,
                    NotAllowPartnerRestrictGuardService,
                    SettingsSaveTonerBehaviorEnabledGuardService,
                    SettingsSaveTonerLicensingEnabledGuardService],
                data: {
                    breadcrumbOptions: {
                        breadcrumbLabel: '360SaveToner',
                    },
                },
            },
        ],
    },
    { path: 'page-not-found', component: PageNotFoundComponent },
    { path: 'page-internal-server-error', component: PageInternalServerErrorComponent },
    { path: 'page-forbidden', component: PageForbiddenComponent },
    { path: 'page-unauthorized', component: PageUnauthorizedComponent },
    { path: '**', redirectTo: 'page-not-found', pathMatch: 'full' },
];

@NgModule({
    imports: [RouterModule.forRoot(appRoutes)],
    exports: [RouterModule],
})
export class AppRoutingModule { }
